import {
  ContactInfo,
  ExtendedShareholderDTO,
  Gender,
  getGenderLabel,
  PersonInfo,
} from "@/types/Shareholder";
import {
  MAX_BIC_LENGTH,
  MAX_COMPANY_NUMBER_LENGTH,
  MAX_IBAN_LENGTH,
} from "@/types/shareholderFieldLengths";
import {
  contactInfoNameValidator,
  personNameValidator,
  phoneValidatorAllowingEmpty,
  utlNumberValidator,
} from "@/utils/other";
import { formatPhoneNumberToE164 } from "@/utils/phoneNumbers";
import { makeAutoObservable } from "mobx";
import { Address, emailValidator } from "@/common/common";
import { idnr } from "@navikt/fnrvalidator";
import { isEmpty } from "lodash";
import { getFullName } from "@/utils/Roles";
import { lookupNorwegianCity } from "@/utils/postalCodes";

import { isValidDate } from "@/utils/Dates";
import { getBooleanLabel } from "./editSingleShareholderDomain";
import { Language } from "@/i18n/languages";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { ParsedShareholder, ParsedShareholderData } from "@/types/excelImportDomain";
import { norway } from "@/utils/countries";
import {
  LocalDateFieldModel,
  maxLengthValidator,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { isBlank, isNotBlank } from "@/utils/strings";

export class ShareholderModel {
  id: number;
  nameModel: StringFieldModel;
  nationalityModel: StringFieldModel;
  contactInfoNameModel: StringFieldModel;
  contactInfoNationalIdModel: StringFieldModel;
  emailModel: StringFieldModel;
  phoneModel: StringFieldModel;
  nationalIdModel: StringFieldModel;
  companyNumberModel: StringFieldModel;
  addressModel: StringFieldModel;
  postalCodeModel: StringFieldModel;
  cityModel: StringFieldModel;
  countryModel: StringFieldModel;
  genderModel: RadioFieldModel<Gender | null>;
  isEmployeeModel: RadioFieldModel<boolean | null>;
  utlNumberModel: StringFieldModel;
  lockupEndDateModel: LocalDateFieldModel;
  loanAmountModel: NumberFieldModel;
  issuedShareholderCertificate: StringFieldModel;
  bicModel: StringFieldModel;
  ibanModel: StringFieldModel;
  remarkModel: StringFieldModel;
  bankDetailsCountryModel: StringFieldModel;
  bankAccountNameModel: StringFieldModel;
  bankPaymentReferenceModel: StringFieldModel;
  bankCountryModel: StringFieldModel;
  bankNameModel: StringFieldModel;
  bankRoutingCodeModel: StringFieldModel;
  bankAddressLine1Model: StringFieldModel;
  bankAddressLine2Model: StringFieldModel;
  bankPostalCodeModel: StringFieldModel;
  bankCityModel: StringFieldModel;
  bankStateModel: StringFieldModel;
  readonly data: ExtendedShareholderDTO;
  readonly isUnidentified: boolean;
  shareholderId: number;
  companyNumber: string | undefined;
  contactInfo?: ContactInfo;
  personInfo?: PersonInfo;
  shareholderAddress: Address;
  isCompany: boolean;

  initNationalId?: boolean;

  constructor(
    data: ExtendedShareholderDTO,
    readonly hasShares: boolean,
    initNationalId?: boolean,
  ) {
    this.initNationalId = initNationalId;
    this.data = data;
    this.isUnidentified = data.isUnidentified === true;
    this.id = data.id;
    this.companyNumber = data.companyNumber;
    this.shareholderId = data.id;
    this.contactInfo = data.contactInfo;
    this.personInfo = data.personInfo;
    this.shareholderAddress = data.address ?? {};
    this.isCompany = data.isCompany;
    this.nameModel = new StringFieldModel({
      validator: data.isCompany ? undefined : personNameValidator,
      required: true,
      value: data.isCompany ? data.companyName : getFullName(data.personInfo),
    });
    this.contactInfoNameModel = new StringFieldModel({
      validator: data.isCompany ? contactInfoNameValidator : undefined,
      value: data.isCompany ? getFullName(data.contactInfo) : "",
    });
    this.contactInfoNationalIdModel = new StringFieldModel({
      value: initNationalId ? data.contactInfo?.socialSecurityNumber : undefined,
    });
    const personInfo = this.isCompany ? data.contactInfo : data.personInfo;
    this.emailModel = new StringFieldModel({
      validator: emailValidator,
      value: personInfo?.email,
    });
    this.phoneModel = new StringFieldModel({
      validator: phoneValidatorAllowingEmpty,
      value: formatPhoneNumberToE164({
        value: personInfo?.phoneNumber?.value ?? "",
        countryCode: personInfo?.phoneNumber?.countryCode ?? "",
      }),
    });

    this.nationalIdModel = new StringFieldModel({
      value: initNationalId ? data.personInfo?.socialSecurityNumber : undefined,
      validator: (s) => {
        switch (true) {
          case this.nationalIdModel.isEmpty && !this.nationalIdModel.isChanged:
            return undefined;
          case this.isSwedishShareholder && !this.isValidSwedishNationalId(s):
            return { key: "invalidSwedishNationalId" };
          case this.isNorwegianShareholder && !this.isValidNorwegianNationalId(s):
            return { key: "invalidNorwegianNationalId" };
          default:
            return undefined;
        }
      },
    });
    this.nationalityModel = new StringFieldModel({
      required: true,
      value: data.nationality ?? Language.NO.toUpperCase(),
    });
    this.companyNumberModel = new StringFieldModel({
      value: data.companyNumber,
      validator: (s) => {
        if (this.isNorwegianShareholder && s && s.length !== 9) {
          return { key: "invalidNorwegianOrgNo" };
        }
        return maxLengthValidator(MAX_COMPANY_NUMBER_LENGTH)(s);
      },
    });
    this.addressModel = new StringFieldModel({
      value: data.address?.address,
    });
    this.postalCodeModel = new StringFieldModel({
      validator: postalCodeValidator(() => this.countryModel.validatedValue),
      value: data.address?.postalCode,
    });
    this.cityModel = new StringFieldModel({
      value: data.address?.city,
    });
    this.countryModel = new StringFieldModel({
      required: true,
      value: data.address?.country,
    });
    this.genderModel = new RadioFieldModel<Gender | null>({
      value: (data.isCompany ? data.contactInfo?.gender : data.personInfo?.gender) || null,
      options: [null, "male", "female"],
      idFn: (value) => String(value),
      labelFn: (value) => getGenderLabel(value),
    });
    this.isEmployeeModel = new RadioFieldModel<boolean | null>({
      value: data.isEmployee === undefined ? null : data.isEmployee,
      options: [null, true, false],
      idFn: (value) => String(value),
      labelFn: (value) => getBooleanLabel(value),
    });
    this.utlNumberModel = new StringFieldModel({
      value: data.utlNumber,
      validator: utlNumberValidator,
    });
    this.lockupEndDateModel = new LocalDateFieldModel({
      value: data.lockupEndDate,
    });
    this.loanAmountModel = new NumberFieldModel({
      value: data.loanAmount,
      isPositiveOrZero: true,
    });
    this.issuedShareholderCertificate = new StringFieldModel({
      value: String(data.issuedShareholderCertificate),
    });
    this.bicModel = new StringFieldModel({
      value: data.bic,
      validator: maxLengthValidator(MAX_BIC_LENGTH),
    });
    this.ibanModel = new StringFieldModel({
      value: data.iban,
      validator: maxLengthValidator(MAX_IBAN_LENGTH),
    });
    this.remarkModel = new StringFieldModel({
      value: data.remark,
    });
    this.bankDetailsCountryModel = new StringFieldModel({
      value: data.bankDetailsCountry,
    });
    this.bankAccountNameModel = new StringFieldModel({ value: data.bankAccountName });
    this.bankPaymentReferenceModel = new StringFieldModel({ value: data.bankPaymentReference });
    this.bankCountryModel = new StringFieldModel({ value: data.bankCountry });
    this.bankNameModel = new StringFieldModel({ value: data.bankName });
    this.bankRoutingCodeModel = new StringFieldModel({ value: data.bankRoutingCode });
    this.bankAddressLine1Model = new StringFieldModel({ value: data.bankAddressLine1 });
    this.bankAddressLine2Model = new StringFieldModel({ value: data.bankAddressLine2 });
    this.bankPostalCodeModel = new StringFieldModel({ value: data.bankPostalCode });
    this.bankCityModel = new StringFieldModel({ value: data.bankCity });
    this.bankStateModel = new StringFieldModel({ value: data.bankState });
    makeAutoObservable(this);
  }

  validate(shareholders: ShareholderModel[] = []) {
    this.nationalityModel.validate();
    this.nameModel.validate();
    this.emailModel.validate();
    this.phoneModel.validate();
    this.addressModel.validate();
    this.postalCodeModel.validate();
    this.cityModel.validate();
    this.countryModel.validate();
    this.bicModel.validate();
    this.ibanModel.validate();

    if (this.isCompany) {
      this.companyNumberModel.validate();
      this.validateDuplicateCompanyNumber(shareholders);
      this.contactInfoNameModel.validate();
    } else {
      this.nationalIdModel.validate();
      this.validateSsn();
    }
    return this.isValid;
  }

  get isValid() {
    return (
      this.nationalityModel.isValid &&
      this.nameModel.isValid &&
      this.emailModel.isValid &&
      this.phoneModel.isValid &&
      this.addressModel.isValid &&
      this.postalCodeModel.isValid &&
      this.cityModel.isValid &&
      this.countryModel.isValid &&
      this.bicModel.isValid &&
      this.ibanModel.isValid &&
      (this.isCompany
        ? this.companyNumberModel.isValid && this.contactInfoNameModel.isValid
        : this.nationalIdModel.isValid)
    );
  }

  validateSsn() {
    if (!this.nationalIdModel.isEmpty) {
      const ssn = this.nationalIdModel.inputValue;
      if (this.isNorwegianShareholder) {
        if (!isValidNorwegianNationalId(ssn)) {
          this.nationalIdModel.setError({ key: "invalidNorwegianNationalId" });
        }
      }
      if (this.isSwedishShareholder) {
        if (!isValidSwedishNationalId(ssn)) {
          this.nationalIdModel.setError({ key: "invalidSwedishNationalId" });
        }
      }
    }
  }

  validateDuplicateCompanyNumber(shareholders: ShareholderModel[]) {
    const isDuplicate = shareholders.some(
      (s) =>
        s.shareholderId !== this.shareholderId &&
        !isEmpty(s.companyNumberModel.inputValue) &&
        !isEmpty(this.companyNumberModel.inputValue) &&
        s.companyNumberModel.inputValue === this.companyNumberModel.inputValue,
    );
    if (isDuplicate) {
      this.companyNumberModel.setError({ key: "nonUniqueCompanyNumber" });
    }
    return !isDuplicate;
  }

  get isSwedishShareholder(): boolean {
    return this.nationalityModel.inputValue === "SE";
  }

  get isNorwegianShareholder(): boolean {
    return this.nationalityModel.inputValue === norway;
  }

  isValidSwedishNationalId(s: string): boolean {
    return this.isSwedishShareholder && isValidSwedishNationalId(s);
  }

  isValidNorwegianNationalId(s: string): boolean {
    return this.isNorwegianShareholder && isValidNorwegianNationalId(s);
  }

  prepareImportData(parsedShareholderData: ParsedShareholderData[]): ParsedShareholder {
    const parsedShareholder = parsedShareholderData.find((s) => s.row.id === this.id);
    return {
      id: this.id,
      type: this.isCompany ? "selskap" : "person",
      name: this.nameModel.inputValue,
      identification: this.isCompany
        ? this.companyNumberModel.inputValue
        : this.nationalIdModel.inputValue,
      representativeName: this.contactInfoNameModel.inputValue,
      representativeSsn: this.contactInfoNationalIdModel.inputValue,
      address: this.addressModel.inputValue,
      postalCode: this.postalCodeModel.inputValue,
      city: this.cityModel.inputValue,
      country: this.countryModel.inputValue,
      email: this.emailModel.inputValue,
      phoneNumber: this.phoneModel.inputValue,
      utlNumber: this.utlNumberModel.inputValue,
      lockupEndDate: this.lockupEndDateModel.inputValue,
      loanAmount: this.loanAmountModel.parsedValue?.toString() ?? "",
      gender: this.genderModel.value?.toString() ?? "",
      isEmployee: this.isEmployeeModel.value === true ? "y" : "",
      bankDetailsCountry: this.bankDetailsCountryModel.inputValue,
      bic: this.bicModel.inputValue,
      iban: this.ibanModel.inputValue,
      remark: this.remarkModel.inputValue,
      numberOfShares: parsedShareholder?.row?.numberOfShares ?? "",
      shareNumbers: parsedShareholder?.row?.shareNumbers ?? "",
      entryDate: parsedShareholder?.row?.entryDate ?? "",
      numberOfPreferenceShares: parsedShareholder?.row?.numberOfPreferenceShares ?? "",
      numberOfAShares: parsedShareholder?.row?.numberOfAShares ?? "",
      numberOfBShares: parsedShareholder?.row?.numberOfBShares ?? "",
      numberOfCShares: parsedShareholder?.row?.numberOfCShares ?? "",
    };
  }

  get maskedNationalId(): TMessageParam | undefined {
    return maskedNationalId(this.nationalIdModel, this.data.personInfo);
  }

  get maskedNationalIdMessage(): TMessage | undefined {
    return maskedNationalIdMessage(this.nationalIdModel, this.data.personInfo, this.initNationalId);
  }

  get maskedContactInfoNationalId(): TMessageParam | undefined {
    return maskedNationalId(this.contactInfoNationalIdModel, this.data.contactInfo);
  }

  get maskedContactInfoNationalIdMessage(): TMessage | undefined {
    return maskedNationalIdMessage(
      this.contactInfoNationalIdModel,
      this.data.contactInfo,
      this.initNationalId,
    );
  }

  hasIncompleteData() {
    if (!this.isNorwegianShareholder && !this.isSwedishShareholder) {
      return false;
    }

    if (this.isCompany) {
      return isBlank(this.companyNumber);
    }

    const ssn = this.personInfo?.socialSecurityNumber;
    if (!ssn) {
      return true;
    }
    if (this.isNorwegianShareholder) {
      return !this.isValidNorwegianNationalId(ssn);
    }
    if (this.isSwedishShareholder) {
      return !this.isValidSwedishNationalId(ssn);
    }
  }
}

function maskedNationalId(
  inputModel: StringFieldModel,
  dataModel: { socialSecurityNumber?: string } | undefined,
): TMessageParam | undefined {
  switch (true) {
    case inputModel.isEmpty && !inputModel.isRebased && isNotBlank(dataModel?.socialSecurityNumber):
      return { type: "ssn", value: dataModel.socialSecurityNumber };
    case !inputModel.isEmpty && inputModel.isValid:
      return { type: "ssn", value: inputModel.validatedValue };
  }
}

function maskedNationalIdMessage(
  inputModel: StringFieldModel,
  dataModel: { socialSecurityNumber?: string } | undefined,
  initNationalId?: boolean,
): TMessage | undefined {
  if (
    initNationalId !== true &&
    !inputModel.isRebased &&
    isNotBlank(dataModel?.socialSecurityNumber)
  ) {
    return { key: "newShareholder.masked-ssn-info" };
  }
}

export function validateAllShareholders(shareholders: ShareholderModel[]) {
  shareholders.forEach((s) => s.validate(shareholders));
}

export function isValidNorwegianNationalId(nationalId: string): boolean {
  return idnr(nationalId).status === "valid";
}

export function isValidSwedishNationalId(nationalId: string): boolean {
  return /^\d{12}$/.test(nationalId) && isValidDate(nationalId.substring(0, 8));
}

export const postalCodeValidator = (countryProvider: () => string) => {
  return (value: string): TMessage | undefined => {
    if (value.trim().length === 0) {
      return undefined;
    } else if (countryProvider() === norway && lookupNorwegianCity(value) === undefined) {
      return { key: "invalidPostalCode" };
    } else {
      return undefined;
    }
  };
};
